/**
 * Annotation-class registry. Each labeling tool (bbox, polygon,
 * keypoint, mask) colour-codes annotations by the currently-active
 * class. The default set covers the most common medical-imaging
 * label targets; organisations can replace the store with their
 * own controlled vocabulary (SNOMED CT mapping, RadLex, etc.)
 * without touching the tool plumbing.
 */
import { createSignal } from "solid-js";

export interface AnnotationClass {
  id:       string;  // stable identifier
  name:     string;  // display label
  color:    string;  // hex, used for stroke + fill
  shortcut?: string; // keyboard shortcut (single character)
}

const DEFAULTS: AnnotationClass[] = [
  { id: "lesion",     name: "Lesion",      color: "#EF4444", shortcut: "1" },
  { id: "tumor",      name: "Tumor",       color: "#F97316", shortcut: "2" },
  { id: "organ",      name: "Organ",       color: "#10B981", shortcut: "3" },
  { id: "vessel",     name: "Vessel",      color: "#3B82F6", shortcut: "4" },
  { id: "bone",       name: "Bone",        color: "#E5E7EB", shortcut: "5" },
  { id: "other",      name: "Other",       color: "#A855F7", shortcut: "6" },
];

const [classes, setClasses] = createSignal<AnnotationClass[]>([...DEFAULTS]);
const [activeId, setActiveId] = createSignal<string>(DEFAULTS[0].id);

export { classes, activeId, setActiveId };

export function activeClass(): AnnotationClass {
  const id = activeId();
  return classes().find((c) => c.id === id) ?? classes()[0];
}

export function classById(id: string | undefined): AnnotationClass | undefined {
  if (!id) return undefined;
  return classes().find((c) => c.id === id);
}

export function addClass(c: AnnotationClass) {
  setClasses((xs) => [...xs, c]);
}

export function removeClass(id: string) {
  setClasses((xs) => xs.filter((c) => c.id !== id));
}

export function setClassList(xs: AnnotationClass[]) {
  setClasses(xs);
  if (!xs.find((c) => c.id === activeId()) && xs.length > 0) {
    setActiveId(xs[0].id);
  }
}
